<template>
  <main-template>
    <v-row class="mb-1">
      <v-col>
        <h1>
          <v-btn color="primary" x-small text @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          Justificativa em Massa
        </h1>
      </v-col>
    </v-row>

    <v-row class="mb-3">
      <v-col cols="auto">
        <v-btn color="primary" :to="{ name: 'colaboradores.justificativaMassa.nova' }">
          <v-icon left>mdi-plus</v-icon>
          Nova Justificativa
        </v-btn>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="justificativas"
          :loading="carregando"
          no-data-text="Nenhuma justificativa em massa registrada"
        >
          <template #[`item.data_referencia`]="{ item }">
            <span class="text-no-wrap">{{ formatarPeriodo(item) }}</span>
          </template>
          <template #[`item.responsavel`]="{ item }">
            {{ item.responsavel && item.responsavel.name ? item.responsavel.name : "—" }}
          </template>
          <template #[`item.itens_count`]="{ item }">
            {{ item.itens_count }} colaborador(es)
          </template>
          <template #[`item.acoes`]="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn icon small color="primary" v-on="on" @click="visualizar(item)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>Visualizar</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn icon small color="warning" v-on="on" @click="editar(item)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Editar</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn icon small color="error" v-on="on" @click="confirmarExclusao(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Remover</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialogVisualizar" max-width="600">
      <v-card class="rounded-lg" v-if="itemVisualizado">
        <v-card-title
          class="primary white--text py-3 px-4 d-flex align-center justify-space-between"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-account-group-outline</v-icon>
            <span class="text-h6 font-weight-bold">Detalhes da Justificativa em Massa</span>
          </div>
          <v-chip small color="info" class="font-weight-bold white--text">
            {{ colaboradoresVisualizados.length }} Colaboradores
          </v-chip>
        </v-card-title>

        <v-card-text class="pt-4 pb-2 px-4">
          <v-card outlined class="mb-4 grey lighten-5">
            <v-card-text class="py-3">
              <v-row dense class="caption mb-2">
                <v-col cols="6"
                  >Período:
                  <strong class="primary--text">{{
                    formatarPeriodo(itemVisualizado)
                  }}</strong></v-col
                >
                <v-col cols="6"
                  >Responsável:
                  <strong>{{ itemVisualizado.responsavel?.name || "—" }}</strong></v-col
                >
              </v-row>
              <v-divider class="my-2"></v-divider>
              <div class="caption font-weight-bold grey--text">Motivo Informado:</div>
              <div class="body-2 font-italic grey--text text--darken-3 mt-1">
                "{{ itemVisualizado.motivo }}"
              </div>
              <div v-if="itemVisualizado.observacao" class="mt-2">
                <div class="caption font-weight-bold grey--text">Observação:</div>
                <div class="body-2 grey--text text--darken-2 mt-1">
                  {{ itemVisualizado.observacao }}
                </div>
              </div>
            </v-card-text>
          </v-card>

          <div class="subtitle-2 font-weight-bold primary--text mb-2">Colaboradores Vinculados</div>
          <div class="pa-2 grey lighten-4 rounded border">
            <v-chip
              v-for="c in colaboradoresVisualizados"
              :key="c.id"
              small
              color="primary"
              outlined
              class="mr-1 mt-1 font-weight-bold"
            >
              <v-icon left x-small>mdi-account</v-icon>
              {{ c.name }}
            </v-chip>
            <span v-if="!colaboradoresVisualizados.length" class="grey--text caption"
              >Nenhum colaborador vinculado</span
            >
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 px-4 bg-grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey" class="font-weight-bold" @click="dialogVisualizar = false"
            >Fechar</v-btn
          >
        </v-card-actions>
      </v-card>
      <v-card class="rounded-lg" v-else>
        <v-card-text class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogExclusao" max-width="450">
      <v-card class="rounded-lg">
        <v-card-title class="error white--text py-3 px-4 d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-alert-circle-outline</v-icon>
          <span class="text-h6 font-weight-bold">Confirmar Remoção</span>
        </v-card-title>
        <v-card-text class="pt-4 pb-2 px-4">
          <div class="body-1 text--primary">
            Deseja remover a justificativa em massa de
            <strong>{{ formatarPeriodo(itemParaExcluir) }}</strong
            >?
          </div>
          <v-alert type="warning" dense text class="mt-3 mb-0">
            Todos os colaboradores vinculados serão desvinculados desta justificativa.
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="py-3 px-4 bg-grey lighten-4">
          <v-btn text color="grey" :disabled="excluindo" @click="dialogExclusao = false"
            >Cancelar</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="error" class="font-weight-bold px-4" :loading="excluindo" @click="excluir">
            <v-icon left small>mdi-delete</v-icon>
            Remover
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
export default {
  name: "JustificativaMassaPage",
  data() {
    return {
      justificativas: [],
      carregando: false,
      dialogVisualizar: false,
      itemVisualizado: null,
      dialogExclusao: false,
      excluindo: false,
      itemParaExcluir: null,
      headers: [
        { text: "Período", value: "data_referencia", class: "text-no-wrap", width: "200" },
        { text: "Motivo", value: "motivo" },
        { text: "Responsável", value: "responsavel", class: "text-no-wrap" },
        { text: "Colaboradores", value: "itens_count" },
        { text: "", value: "acoes", sortable: false, align: "center", width: "120" },
      ],
    };
  },
  computed: {
    colaboradoresVisualizados() {
      if (!this.itemVisualizado) return [];
      return (this.itemVisualizado.itens || []).map((i) => i.colaborador).filter(Boolean);
    },
  },
  mounted() {
    this.carregar();
  },
  methods: {
    async carregar() {
      try {
        this.carregando = true;
        this.justificativas =
          await this.$services.colaboradorPontosService.listarJustificativasMassa();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregando = false;
      }
    },
    editar(item) {
      this.$router.push({
        name: "colaboradores.justificativaMassa.nova",
        query: { editarId: item.id },
      });
    },
    async visualizar(item) {
      this.itemVisualizado = null;
      this.dialogVisualizar = true;
      try {
        this.itemVisualizado =
          await this.$services.colaboradorPontosService.buscarJustificativaMassa(item.id);
      } catch (error) {
        this.$handleError(error);
        this.dialogVisualizar = false;
      }
    },
    confirmarExclusao(item) {
      this.itemParaExcluir = item;
      this.dialogExclusao = true;
    },
    async excluir() {
      try {
        this.excluindo = true;
        await this.$services.colaboradorPontosService.excluirJustificativaMassa(
          this.itemParaExcluir.id
        );
        this.$toast.success("Justificativa em massa removida com sucesso!");
        this.dialogExclusao = false;
        await this.carregar();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.excluindo = false;
      }
    },
    formatarData(data) {
      if (!data) return "—";
      const [ano, mes, dia] = String(data).split("T")[0].split("-");
      return `${dia}/${mes}/${ano}`;
    },
    formatarPeriodo(item) {
      if (!item) return "—";
      const inicio = this.formatarData(item.data_referencia);
      const fim = item.data_fim ? this.formatarData(item.data_fim) : inicio;
      return fim === inicio ? inicio : `${inicio} até ${fim}`;
    },
  },
};
</script>
